// Telling apart objects that are shown under the same name.
//
// A relationship can reach several objects whose titles are identical. In a
// semantic layer that is the normal case rather than a fault: five
// `ModelElement`s each model the same activity in a different source model,
// all carrying the same `label` and no `name` of their own. The product showed
// the title and nothing else, so five different things read as five copies of
// one - and a reviewer reasonably asked why the explorer was duplicating his
// data (7 Oct 2026). It wasn't: the thing that told them apart (`uri`, `id`)
// was simply never shown.
//
// **No attribute name is in this file.** The one that tells them apart is
// whichever attribute actually differs between them, found by looking - so it
// works on a library nobody here has seen, and on the next one.
//
// Pure: no React, no DOM, no network.
import { attributeLabel, displayValue } from "../../utils/viewConfig";

// The product's own keys, which say nothing about which object this is.
// Everything beginning `_` is bookkeeping the product itself writes.
const OURS = new Set(["GUID", "Type", "name", "hasAssignment", "inTrial", "formats", "diagrams", "hasDiagram"]);

// Long enough to recognise one, short enough to sit under a title.
const MAX = 40;

// A value a reader can take in. An address is the same prefix on every sibling
// and differs only at its end, so the end is what is shown - a general rule
// about anything written as a path, not a rule about one attribute.
const readable = (raw) => {
  const text = displayValue(raw).trim();
  if (!text) return "";
  const parts = text.split("/").filter(Boolean);
  const tail = parts.length > 2 ? parts.slice(-2).join("/") : text;
  return tail.length > MAX ? `${tail.slice(0, MAX)}…` : tail;
};

const candidateKeys = (group) => {
  const keys = new Set();
  group.forEach(({ item }) => {
    Object.keys(item || {}).forEach((key) => {
      if (key.startsWith("_") || OURS.has(key)) return;
      keys.add(key);
    });
  });
  return [...keys].sort();
};

// How well one attribute separates a group: how many of its members it gives a
// value nobody else has. An attribute every member shares separates nothing.
const separation = (group, key) => {
  const seen = new Map();
  group.forEach(({ item }) => {
    const value = readable(item?.[key]);
    if (!value) return;
    seen.set(value, (seen.get(value) || 0) + 1);
  });
  return [...seen.values()].filter((n) => n === 1).length;
};

// For every object shown under a name it shares with another, the attribute
// that tells it apart and its value. Objects with a name of their own, and
// groups where nothing differs, get nothing - saying nothing is honest, and
// each one can still be opened.
export const distinguishers = (items) => {
  const found = new Map();
  const byName = new Map();
  (items || []).forEach((entry) => {
    if (!entry?.guid) return;
    const name = String(entry.name || "").trim();
    if (!name) return;
    if (!byName.has(name)) byName.set(name, []);
    byName.get(name).push(entry);
  });

  byName.forEach((group) => {
    if (group.length < 2) return;
    const name = String(group[0].name || "").trim();
    const keys = candidateKeys(group).filter((key) =>
      // The attribute the title was read from says nothing: it *is* the title.
      group.some(({ item }) => displayValue(item?.[key]).trim() !== name)
    );
    let best = null;
    keys.forEach((key) => {
      const score = separation(group, key);
      if (score > (best?.score || 0)) best = { key, score };
    });
    if (!best || best.score < 2) return;
    group.forEach((entry) => {
      const value = readable(entry.item?.[best.key]);
      if (value) found.set(entry.guid, { key: best.key, label: attributeLabel(best.key), value });
    });
  });

  return found;
};
